import { Grid } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import InputAdornment from '@mui/material/InputAdornment'
import { useFormikContext } from 'formik'
import TransformDisplayToEdition from '../../../../components/form/TransformDisplayToEdition'
import mapEnumForSelect from '../../../../utils/mapEnumForSelect'
import { CostType } from '../../../../__generated__/graphql'
import { displayAsCurrency } from '../../../../utils/displayAsCurrency'
import { FieldsProps } from '.'
import { useUserPermissions } from '../../../company/userPermissions'
import { FieldGroup, fieldGroupItemSize } from './FieldGroup'
import { AdgroupExtendedSettingsFormInterface } from '../Settings'

const isCostTypeAllowed = (type: CostType, permissions: { adgroupCpl: boolean; adgroupCpm: boolean }): boolean => {
  if (type === CostType.Cpl || type === CostType.Cpo) return permissions.adgroupCpl
  if (type === CostType.Cpm) return permissions.adgroupCpm
  return true
}

const Cost: React.FC<FieldsProps> = ({
  adgroup,
  isWizard = false,
  canEdit = true,
  fullWidth = false,
  allowUnchanged = false,
}): JSX.Element => {
  const { adgroupCpl, adgroupCpm } = useUserPermissions()
  const { values, setFieldValue } = useFormikContext<AdgroupExtendedSettingsFormInterface>()

  const costTypeOptions = useMemo(
    () =>
      mapEnumForSelect(CostType)
        .filter((type) => isCostTypeAllowed(type.value, { adgroupCpl, adgroupCpm }))
        .map((type) => ({ value: type.value, label: type.label.toUpperCase() })),
    [adgroupCpl, adgroupCpm]
  )

  const isCostTypeSelectLocked = !allowUnchanged && costTypeOptions.length <= 1
  const soleCostType = costTypeOptions[0]?.value

  useEffect(() => {
    if (allowUnchanged) return
    if (isCostTypeSelectLocked && soleCostType && values.costType !== soleCostType) {
      void setFieldValue('costType', soleCostType)
    }
  }, [allowUnchanged, isCostTypeSelectLocked, soleCostType, values.costType])

  return (
    <FieldGroup size={fullWidth ? 6 : 3}>
      <Grid size={fieldGroupItemSize}>
        <TransformDisplayToEdition
          inputType="input"
          type="number"
          name="costValue"
          required={!allowUnchanged}
          label="adgroup.details.costValueWithCurrency"
          text={displayAsCurrency(adgroup.costValue)}
          InputProps={{
            endAdornment: <InputAdornment position="end">€</InputAdornment>,
            inputProps: { step: 0.01 },
          }}
          disableHoverEdition={isWizard}
          editable={canEdit}
        />
      </Grid>
      <Grid size={fieldGroupItemSize}>
        <TransformDisplayToEdition
          inputType="select"
          withNone={allowUnchanged}
          noneTranslation="adgroup.budgetAndDates.budgetPlans.unchanged"
          name="costType"
          required={!allowUnchanged}
          label="adgroup.details.costType"
          text={allowUnchanged ? String(values.costType ?? '').toUpperCase() : adgroup.costType?.toUpperCase()}
          disableHoverEdition={isWizard}
          editable={canEdit}
          disabled={isCostTypeSelectLocked}
          options={costTypeOptions}
        />
      </Grid>
    </FieldGroup>
  )
}
export default Cost
